import { useEffect } from "react";
import { createPortal } from "react-dom";
import { Copy, ExternalLink, X } from "lucide-react";
import { useToast } from "../ToastContext";

function getInitials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("") || "?";
}

export function OperatorAvatar({ name, className = "" }) {
  return (
    <span
      title={name}
      className={`flex items-center justify-center rounded-full bg-linear-to-br from-emerald-400 to-green-600 font-bold text-emerald-950 ${className}`}
    >
      {getInitials(name)}
    </span>
  );
}

function OperatorRow({ member }) {
  const { showToast } = useToast();
  const url = member.attackbox_url;
  const actionClass =
    "inline-flex items-center gap-1 h-7 px-2.5 rounded-md border text-[11px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      showToast("AttackBox URL copied", "success");
    } catch {
      showToast("Could not copy URL", "error");
    }
  };

  return (
    <li className="flex items-center gap-3 px-5 py-2.5 hover:bg-white/2 transition-colors">
      <OperatorAvatar
        name={member.name || member.email}
        className="h-8 w-8 text-[10px] shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-(--color-text-primary) truncate">
          {member.name || member.email}
        </p>
        <p
          className={`font-mono text-[11px] truncate ${url ? "text-sky-400" : "text-(--color-text-muted)"}`}
          title={url || undefined}
        >
          {url || "No AttackBox running"}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={handleCopy}
          disabled={!url}
          className={`${actionClass} border-(--color-border) text-(--color-text-secondary) hover:bg-white/5`}
        >
          <Copy className="h-3 w-3" />
          Copy
        </button>
        <button
          type="button"
          onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          disabled={!url}
          className={`${actionClass} border-emerald-700/50 bg-emerald-900/20 text-emerald-400 hover:bg-emerald-900/40`}
        >
          <ExternalLink className="h-3 w-3" />
          Open
        </button>
      </div>
    </li>
  );
}

export default function TeamOperatorsModal({ run, members, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const subtitle = [
    run.org_name,
    `${members.length} ${members.length === 1 ? "operator" : "operators"}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        // Fixed height so the dialog doesn't resize with the member count;
        // the list scrolls inside it instead.
        className="flex flex-col w-full max-w-xl h-105 max-h-[85vh] rounded-xl border border-(--color-border) bg-(--color-bg) shadow-2xl overflow-hidden"
      >
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-(--color-border) shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-400">
              Operators
            </p>
            <h2 className="mt-0.5 text-base font-semibold text-(--color-text-primary) truncate">
              {run.team_name || "Unnamed Team"}
            </h2>
            <p className="mt-0.5 text-xs text-(--color-text-muted) truncate">
              {subtitle}
              {run.scenario?.title && (
                <>
                  {" · "}
                  <span className="text-(--color-text-secondary)">
                    {run.scenario.title}
                  </span>
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -m-1 rounded-md text-(--color-text-muted) hover:text-(--color-text-primary) hover:bg-white/5 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {members.length === 0 ? (
          <p className="flex-1 flex items-center justify-center text-xs text-(--color-text-muted)">
            This team has no members.
          </p>
        ) : (
          <ul className="flex-1 min-h-0 overflow-y-auto py-1 divide-y divide-(--color-border)">
            {members.map((member) => (
              <OperatorRow key={member.id} member={member} />
            ))}
          </ul>
        )}
      </div>
    </div>,
    document.body,
  );
}
